import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setConsoleFunction } from 'three';
import { ROOMS, setRooms } from '../places.mjs';
import { sampleServerClock } from './cinema/playbackClock';
import './index.css';
import {viewer} from './viewer';

const root = document.getElementById('root');
if (!root) {
  throw new Error('Missing #root');
}

// React Three Fiber still builds its frame clock on THREE.Clock, which three flags as deprecated
// the moment it is constructed. Not ours to change; that one line is dropped, everything else passes.
setConsoleFunction((type, message, ...params) => {
  if (type === 'warn' && message.startsWith('THREE.Clock: This module has been deprecated')) return;
  console[type](message, ...params);
});

// Cloudflare Web Analytics: visitors, countries, devices, vitals. Production builds only.
const beacon = import.meta.env.VITE_CF_BEACON_TOKEN;
if (beacon) {
  const s = document.createElement('script');
  s.defer = true;
  s.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  s.setAttribute('data-cf-beacon', JSON.stringify({ token: beacon }));
  document.head.appendChild(s);
}

const view=createRoot(root);
async function start() {
  try{await viewer.ready();}catch{view.render(<main className="catalog-notice"><h1>The Infinite</h1><p>Could not connect. Please try again.</p><button onClick={()=>location.reload()}>Try again</button></main>);return;}
  if(!import.meta.env.VITE_INFINITE_CATALOG){
    const {App}=await import('./App');
    view.render(<StrictMode><App/></StrictMode>); return;
  }
  let last=''; let busy=false; let denied=false;
  let privatePreview=false;let privateRoom=false;
  let App: typeof import('./App')['App'];
  let net: typeof import('./net/client')['cinemaNet'];
  function render(problem?:string){
    const path=location.pathname.replace(/\/+$/,'')||'/';
    const room=Object.values(ROOMS).find(r=>r.path===path);
    const message=problem || (path!=='/' && (!room ? privateRoom?'This room is in private preview. Ask its owner for a preview link.':'This room could not be found or is not published.' : room.access_enabled===false ? room.suspension_message||'This room is closed.' : denied ? 'Entry to this room is temporarily unavailable.' : ''));
    if(message){
      net?.disconnect();
      document.exitPointerLock?.();
      view.render(<main className="catalog-notice"><h1>The Infinite</h1><p role="status">{message}</p><a href="/">Return to lobby</a><button onClick={()=>location.reload()}>Try again</button></main>);
    }else if(App){
      view.render(<><App/>{(privatePreview||import.meta.env.VITE_DEPLOY_ENV!=='production')&&<div className="staging-badge">{privatePreview?'PRIVATE PREVIEW':'STAGING'}</div>}</>);
    }
  }
  async function refresh(){
    if(busy)return; busy=true;
    try{
      const slug=location.pathname.replace(/^\/|\/$/g,'');
      const sent=performance.now();
      const r=await fetch(`/api/catalog?room=${encodeURIComponent(slug)}`,{cache:'no-store',signal:AbortSignal.timeout(8000)});
      if(!r.ok)throw Error('Rooms are temporarily unavailable.');
      const data=await r.json(); sampleServerClock(data.server_time,sent,performance.now());
      privatePreview=data.preview===true;privateRoom=data.access==='private';
      if(typeof data.redirect==='string' && /^\/[a-z0-9]+(-[a-z0-9]+)*$/.test(data.redirect)) {location.replace(data.redirect+location.search+location.hash);return;}
      const signature=JSON.stringify(data.rooms);
      if(signature!==last){setRooms(data.rooms);last=signature;denied=false;}
      if(!App){App=(await import('./App')).App;net=(await import('./net/client')).cinemaNet;}
      render();
    }catch(error){console.error('[catalog] Refresh failed',error);render('Rooms are temporarily unavailable.');}
    finally{busy=false;}
  }
  view.render(<main className="catalog-notice"><h1>The Infinite</h1><p>Opening the doors…</p></main>);
  const token=new URLSearchParams(location.hash.slice(1)).get('preview');
  if(token){
    history.replaceState(null,'',location.pathname+location.search);
    try{
      const response=await fetch('/api/preview',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({slug:location.pathname.replace(/^\/|\/$/g,''),token})});
      const result=await response.json();if(!response.ok)throw Error(result.error||'This preview is unavailable.');
      if(result.path!==location.pathname)history.replaceState(null,'',result.path+location.search);
    }catch(e){render(e instanceof Error?e.message:'This preview is unavailable.');return;}
  }
  await refresh();
  setInterval(()=>void refresh(),3000);
  window.addEventListener('infinite-room-denied',()=>{denied=true;void refresh();});
  window.addEventListener('popstate',()=>void refresh());
}
void start();
